import { useEffect, useState } from "react";
import { format, addDays, subDays } from "date-fns";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { ko } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logError } from "@/lib/sentry";

interface OrderDatePickerProps {
  date: Date;
  setDate: (date: Date) => void;
  onDateChange?: (date: Date) => void;
  className?: string;
}

export function OrderDatePicker({ date, setDate, onDateChange, className }: OrderDatePickerProps) {
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);
  // 달력에 실제로 보이는 달. date가 바뀌면 그 달로 되돌리고, 그 사이 사용자의 월 이동은 보존한다.
  const [visibleMonth, setVisibleMonth] = useState<Date>(date);
  useEffect(() => { setVisibleMonth(date); }, [date]);
  const { user } = useAuth();
  
  // 주문 데이터가 있는 날짜 조회
  useEffect(() => {
    async function fetchDatesWithOrders() {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('order')
          .select('order_date')
          .eq('user_id', user.id)
          .order('order_date', { ascending: false });
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          // 중복 날짜 제거
          const uniqueDates = [...new Set(data.map(item => item.order_date))];
          // Date 객체로 변환
          const dates = uniqueDates.map(dateStr => new Date(dateStr));
          setHighlightedDates(dates);
        }
      } catch (err) {
        logError("주문 데이터가 있는 날짜 조회 실패:", err);
      }
    }
    
    fetchDatesWithOrders();
  }, [user]);

  // 이전 날짜로 이동
  const handlePrevDay = () => {
    const newDate = subDays(date, 1);
    setDate(newDate);
    if (onDateChange) onDateChange(newDate);
  };

  // 다음 날짜로 이동
  const handleNextDay = () => {
    const newDate = addDays(date, 1);
    setDate(newDate);
    if (onDateChange) onDateChange(newDate);
  };

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <Button 
        variant="outline" 
        size="icon"
        onClick={handlePrevDay}
        className="h-9 w-9"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      {/* 열 때마다 선택일의 달로 리셋 — visibleMonth가 팝오버 바깥(이 컴포넌트)에 살아서,
          월만 넘겨보고 닫았다 다시 열면 그 달이 남아 선택일이 안 보이는 원래 결함이 재현된다. */}
      <Popover onOpenChange={(open) => { if (open) setVisibleMonth(date); }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "min-w-[180px] justify-center text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "PPP", { locale: ko }) : <span>날짜 선택</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[80] w-auto p-0" align="center">
          <Calendar
            mode="single"
            selected={date}
            // react-day-picker v8은 월을 지정하지 않으면 '오늘 달'을 연다. 선택일이 과거면
            // 피커에 선택 표시조차 안 보여, 날짜가 어긋난 사실을 확인할 방법이 없었다.
            // defaultMonth는 초기값이라 열려 있는 동안 date가 바뀌면 따라가지 않는다 →
            // controlled month + onMonthChange(수동 월 이동 보존) + date 변경 시 재동기.
            month={visibleMonth}
            onMonthChange={setVisibleMonth}
            onSelect={(newDate) => {
              if (newDate) {
                setDate(newDate);
                if (onDateChange) onDateChange(newDate);
              }
            }}
            initialFocus
            highlightedDates={highlightedDates}
          />
        </PopoverContent>
      </Popover>
      
      <Button 
        variant="outline" 
        size="icon"
        onClick={handleNextDay}
        className="h-9 w-9"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
} 